/**
 * Invoice Template Context
 *
 * Provides shared invoice template state backed by the API service layer.
 * The API is the sole source of truth — no localStorage caching.
 * This allows templates edited in Settings > Templates to persist across
 * page navigations and be available to the Invoice Generation Wizard.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import type { InvoiceTemplate } from '@/lib/invoice-template-types';
import {
  getInvoiceTemplates as fetchTemplatesFromApi,
  createInvoiceTemplate as createTemplateApi,
  updateInvoiceTemplate as updateTemplateApi,
  deleteInvoiceTemplate as deleteTemplateApi,
} from '@/services/invoiceTemplateService';

interface InvoiceTemplateContextType {
  templates: InvoiceTemplate[];
  addTemplate: (template: InvoiceTemplate) => void;
  updateTemplate: (id: number, updates: Partial<InvoiceTemplate>) => void;
  deleteTemplate: (id: number) => void;
  setDefault: (id: number) => void;
  getDefaultTemplate: () => InvoiceTemplate | undefined;
  getTemplate: (id: number) => InvoiceTemplate | undefined;
  setTemplates: React.Dispatch<React.SetStateAction<InvoiceTemplate[]>>;
}

const InvoiceTemplateContext = createContext<InvoiceTemplateContextType | undefined>(undefined);

export function InvoiceTemplateProvider({ children }: { children: ReactNode }) {
  const [templates, setTemplates] = useState<InvoiceTemplate[]>([]);

  // Load templates from API on mount
  useEffect(() => {
    let cancelled = false;
    fetchTemplatesFromApi()
      .then((apiTemplates) => {
        if (!cancelled) {
          setTemplates(apiTemplates);
        }
      })
      .catch((err) => {
        console.error('Failed to load invoice templates from API:', err);
      });
    return () => { cancelled = true; };
  }, []);

  const addTemplate = useCallback((template: InvoiceTemplate) => {
    setTemplates(prev => [...prev, template]);
    // Fire-and-forget API call
    createTemplateApi(template).catch(() => {
      // API unavailable — local state is already updated
    });
  }, []);

  const updateTemplate = useCallback((id: number, updates: Partial<InvoiceTemplate>) => {
    setTemplates(prev => {
      const updated = prev.map(template =>
        template.id === id
          ? { ...template, ...updates, updatedAt: new Date() }
          : template
      );
      // Fire-and-forget API call with the merged template
      const merged = updated.find(t => t.id === id);
      if (merged) {
        updateTemplateApi(merged).catch(() => {
          // API unavailable — local state is already updated
        });
      }
      return updated;
    });
  }, []);

  const deleteTemplate = useCallback((id: number) => {
    setTemplates(prev => prev.filter(template => template.id !== id));
    deleteTemplateApi(id).catch(() => {
      // API unavailable — local state is already updated
    });
  }, []);

  const setDefault = useCallback((id: number) => {
    setTemplates(prev => {
      const updated = prev.map(template => ({
        ...template,
        isDefault: template.id === id,
      }));
      // Persist the newly-default template to the API
      const defaultTemplate = updated.find(t => t.id === id);
      if (defaultTemplate) {
        updateTemplateApi(defaultTemplate).catch(() => {
          // API unavailable — local state is already updated
        });
      }
      return updated;
    });
  }, []);

  const getDefaultTemplate = useCallback(() => {
    return templates.find(t => t.isDefault) || templates[0];
  }, [templates]);

  const getTemplate = useCallback((id: number) => {
    return templates.find(template => template.id === id);
  }, [templates]);

  return (
    <InvoiceTemplateContext.Provider
      value={{
        templates,
        addTemplate,
        updateTemplate,
        deleteTemplate,
        setDefault,
        getDefaultTemplate,
        getTemplate,
        setTemplates,
      }}
    >
      {children}
    </InvoiceTemplateContext.Provider>
  );
}

export function useInvoiceTemplates() {
  const context = useContext(InvoiceTemplateContext);
  if (context === undefined) {
    throw new Error('useInvoiceTemplates must be used within an InvoiceTemplateProvider');
  }
  return context;
}
